import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, ChevronLeft, ChevronRight, ImageOff, RotateCcw } from "lucide-react";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { smbDesignSystemsResponseSchema, smbLpCatalogResponseSchema } from "@shared/smb-lp-templates";
import type { SmbManifest } from "@shared/smb";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InertHtmlCardPreview } from "./InertHtmlCardPreview";

const API = "/api/admin/smb";
const PAGE_SIZE = 50;
const WARNING_LABELS: Record<string, string> = {
  unresolved: "Unresolved",
  inactive: "Inactive",
  wrong_source: "Wrong source",
  brand_restricted: "Brand restricted",
  missing_registration: "Missing registration",
  missing_html: "Missing HTML",
  studio_source_unverified: "Studio source unverified",
};
type LandingPageFields = Pick<SmbManifest, "lpTemplateCatalogKeys" | "lpDesignSystemId">;

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "The request could not be completed.";
}

export function LandingPagesSettings({
  value,
  onChange,
  brandId,
  inheritedValues,
  isOverride = false,
  disabled = false,
}: {
  value: LandingPageFields;
  onChange: (patch: LandingPageFields) => void;
  brandId?: string | null;
  inheritedValues?: LandingPageFields;
  isOverride?: boolean;
  disabled?: boolean;
}) {
  const [page, setPage] = useState(1);
  const selectedKeys = value.lpTemplateCatalogKeys ?? (isOverride ? inheritedValues?.lpTemplateCatalogKeys : undefined) ?? [];
  const designSystemId = value.lpDesignSystemId !== undefined
    ? value.lpDesignSystemId
    : isOverride
      ? inheritedValues?.lpDesignSystemId ?? null
      : null;
  const selectedKeyParam = useMemo(() => [...selectedKeys].sort().join(","), [selectedKeys]);
  const catalogQuery = useQuery({
    queryKey: [`${API}/templates/landing-pages`, brandId ?? null, designSystemId, selectedKeyParam, page, PAGE_SIZE],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (brandId) params.set("brandId", brandId);
      if (designSystemId) params.set("designSystemId", designSystemId);
      if (selectedKeyParam) params.set("selectedKeys", selectedKeyParam);
      return smbLpCatalogResponseSchema.parse(await parseJsonResponse(
        await apiRequest("GET", `${API}/templates/landing-pages?${params.toString()}`),
      ));
    },
  });
  const designSystemsQuery = useQuery({
    queryKey: [`${API}/templates/design-systems`],
    queryFn: async () => smbDesignSystemsResponseSchema.parse(await parseJsonResponse(
      await apiRequest("GET", `${API}/templates/design-systems`),
    )),
    staleTime: 60_000,
  });
  const catalog = catalogQuery.data?.items ?? [];
  const keyMap = useMemo(() => new Map(catalog.map((item) => [item.key, item])), [catalog]);
  const unresolved = selectedKeys.filter((key) => !keyMap.has(key));
  const inheritedKeys = inheritedValues?.lpTemplateCatalogKeys ?? [];
  const isInherited = (field: keyof LandingPageFields) => isOverride && !Object.prototype.hasOwnProperty.call(value, field);
  const maxPage = Math.max(1, Math.ceil((catalogQuery.data?.total ?? 0) / PAGE_SIZE));

  function toggleKey(key: string) {
    if (disabled) return;
    const selected = selectedKeys.includes(key);
    if (selected) {
      onChange({ lpTemplateCatalogKeys: selectedKeys.filter((item) => item !== key) });
      return;
    }
    if (selectedKeys.length >= 3) return;
    onChange({ lpTemplateCatalogKeys: [...selectedKeys, key] });
  }

  function selectDesignSystem(raw: string) {
    if (disabled) return;
    onChange({ lpDesignSystemId: raw === "__default__" ? null : raw });
  }

  return <section id="smb-landing-settings" className="rounded-xl border bg-card p-4 sm:p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">Landing pages</h3>
          <Badge variant="outline" className="text-[9px]">{isOverride ? "Order overrides" : "Package defaults"}</Badge>
        </div>
        <p className="max-w-2xl text-[11px] leading-relaxed text-muted-foreground">Saved for later. Landing pages are not generated yet.</p>
      </div>
      <span className="font-mono text-[10px] text-muted-foreground">{selectedKeys.length} / 3 selected</span>
    </div>

    {isOverride && <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2">
      <div className="text-[10px] text-muted-foreground">
        <span className="font-semibold text-foreground">Package values:</span>{" "}
        {inheritedKeys.length ? `${inheritedKeys.length} landing page${inheritedKeys.length === 1 ? "" : "s"}` : "No landing pages"}
        {" · "}
        {inheritedValues?.lpDesignSystemId ?? "Default styling"}
        <span className="ml-2">{isInherited("lpTemplateCatalogKeys") && isInherited("lpDesignSystemId") ? "Currently inheriting" : "Order-specific values"}</span>
      </div>
      <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" disabled={disabled || (isInherited("lpTemplateCatalogKeys") && isInherited("lpDesignSystemId"))} onClick={() => onChange({ lpTemplateCatalogKeys: undefined, lpDesignSystemId: undefined })}>
        <RotateCcw className="mr-1.5 h-3 w-3" />Reset to package
      </Button>
    </div>}

    <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(220px,.8fr)]">
      <div className="rounded-lg border bg-card px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Landing page selections</p><p className="mt-0.5 text-[10px] text-muted-foreground">{isInherited("lpTemplateCatalogKeys") ? "Inherited from package" : "Stored as catalog keys"}</p></div>
          {isOverride && !isInherited("lpTemplateCatalogKeys") && <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[9px]" disabled={disabled} onClick={() => onChange({ lpTemplateCatalogKeys: undefined })}>Use package</Button>}
        </div>
      </div>
      <label className="rounded-lg border bg-card px-3 py-2.5">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Design system</span>
        <span className="mt-0.5 flex items-center gap-2">
          <select aria-label="Landing page design system" value={designSystemId ?? "__default__"} disabled={disabled} onChange={(event) => selectDesignSystem(event.target.value)} className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60">
            <option value="__default__">None (default styling)</option>
            {designSystemId && !designSystemsQuery.data?.designSystems.some((system) => system.id === designSystemId) && <option value={designSystemId}>{designSystemId} · unavailable, retained</option>}
            {(designSystemsQuery.data?.designSystems ?? []).map((system) => <option key={system.id} value={system.id}>{system.name}{system.isReserved ? " · reserved" : ""}</option>)}
          </select>
          {isOverride && !isInherited("lpDesignSystemId") && <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[9px]" disabled={disabled} onClick={() => onChange({ lpDesignSystemId: undefined })}>Use package</Button>}
        </span>
        {isOverride && <span className="mt-1 block text-[9px] text-muted-foreground">{isInherited("lpDesignSystemId") ? "Inherited from package" : "Order-specific value"}</span>}
      </label>
    </div>

    {designSystemsQuery.isError && <div className="mt-2 flex items-start gap-2 rounded-lg border bg-muted/50 p-2.5 text-[10px] text-foreground">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>Design systems could not be loaded: {errorMessage(designSystemsQuery.error)}. Default styling remains available; the saved selection is retained.</span>
      <button type="button" className="ml-auto shrink-0 underline underline-offset-2" onClick={() => void designSystemsQuery.refetch()}>Retry</button>
    </div>}
    {designSystemsQuery.data && !designSystemsQuery.data.available && <div className="mt-2 flex items-start gap-2 rounded-lg border bg-muted/50 p-2.5 text-[10px] text-foreground">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{designSystemsQuery.data.error || "Design systems are unavailable."} Default styling remains available; the saved selection is retained.</span>
    </div>}
    {designSystemsQuery.isLoading && <div className="mt-2 h-8 animate-pulse rounded-lg bg-muted/60" />}

    {unresolved.length > 0 && <div className="mt-3 rounded-lg border bg-muted/50 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider">Saved keys not returned in this catalog page</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">{unresolved.map((key) => <span key={key} className="inline-flex items-center gap-1 rounded border bg-card px-2 py-1 font-mono text-[9px]">{key}<button type="button" aria-label={`Remove ${key}`} disabled={disabled} onClick={() => toggleKey(key)} className="font-sans font-semibold underline underline-offset-2 disabled:opacity-50">Remove</button></span>)}</div>
    </div>}

    {catalogQuery.isError ? <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">{errorMessage(catalogQuery.error)} <Button type="button" size="sm" variant="outline" className="ml-2" onClick={() => void catalogQuery.refetch()}>Retry</Button></div>
      : catalogQuery.isLoading ? <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((item) => <div key={item} className="h-32 animate-pulse rounded-lg bg-muted/60" />)}</div>
        : catalog.length ? <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {catalog.map((item) => {
            const selected = selectedKeys.includes(item.key);
            const atCapacity = !selected && selectedKeys.length >= 3;
            return <button type="button" key={item.key} aria-pressed={selected} disabled={disabled || (!item.eligible && !selected) || atCapacity} onClick={() => toggleKey(item.key)} className={`group min-w-0 overflow-hidden rounded-lg border bg-card text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 ${selected ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "hover:border-primary/40"}`}>
              <div className="relative aspect-[1.8/1] overflow-hidden bg-muted">{item.thumbnail ? <img src={item.thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" /> : item.previewHtml ? <InertHtmlCardPreview html={item.previewHtml} title={`${item.label} preview`} /> : <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center text-muted-foreground"><ImageOff className="h-5 w-5" /><span className="text-[10px]">Preview unavailable</span></div>}{selected && <span className="pointer-events-none absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground"><Check className="h-3 w-3" /></span>}</div>
              <div className="p-2"><p className="truncate text-xs font-medium">{item.label}</p>{item.description && <p className="mt-0.5 line-clamp-2 text-[10px] leading-relaxed text-muted-foreground">{item.description}</p>}<p className="mt-1 truncate font-mono text-[9px] text-muted-foreground">{item.key}</p>{!item.eligible && <p className="mt-1 text-[9px] font-medium text-muted-foreground">Unavailable · {item.warnings.map((warning) => WARNING_LABELS[warning] ?? warning).join(" · ")}</p>}</div>
            </button>;
          })}
        </div> : <div className="mt-3 rounded-lg border border-dashed px-4 py-8 text-center text-xs text-muted-foreground">No landing page entries are available.</div>}
    <div className="mt-3 flex items-center justify-between border-t pt-3 text-[10px] text-muted-foreground">
      <span>Catalog page {catalogQuery.data?.page ?? page} of {maxPage} · {catalogQuery.data?.total ?? 0} entries</span>
      <div className="flex gap-1"><Button type="button" size="icon" variant="outline" className="h-7 w-7" aria-label="Previous landing page catalog page" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft className="h-3.5 w-3.5" /></Button><Button type="button" size="icon" variant="outline" className="h-7 w-7" aria-label="Next landing page catalog page" disabled={!catalogQuery.data || page >= maxPage} onClick={() => setPage((current) => current + 1)}><ChevronRight className="h-3.5 w-3.5" /></Button></div>
    </div>
  </section>;
}
